/**
 * AgentStatusCard Component (Issue #157)
 *
 * Displays the status of a single agent during analysis,
 * showing progress, current step, and expandable logs.
 */

import { useState } from 'react';
import type { AgentRun, AgentStatus } from '../../services/api';

interface AgentStatusCardProps {
  agent: AgentRun;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

// Status icons and colors
const statusConfig: Record<AgentStatus, { icon: string; color: string; bgColor: string }> = {
  idle: { icon: '⏳', color: 'text-slate-400', bgColor: 'bg-slate-100 dark:bg-slate-800' },
  analyzing: { icon: '🔍', color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/30' },
  generating: { icon: '✨', color: 'text-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-900/30' },
  complete: { icon: '✅', color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-900/30' },
  error: { icon: '❌', color: 'text-red-500', bgColor: 'bg-red-50 dark:bg-red-900/30' },
};

export function AgentStatusCard({ agent, isExpanded = false, onToggleExpand }: AgentStatusCardProps) {
  const [showInsights, setShowInsights] = useState(false);
  const config = statusConfig[agent.status];

  // Format status text
  const getStatusText = () => {
    switch (agent.status) {
      case 'idle':
        return 'Waiting...';
      case 'analyzing':
        return agent.currentStep || 'Analyzing...';
      case 'generating':
        return agent.currentStep || 'Generating diagram...';
      case 'complete':
        return 'Complete';
      case 'error':
        return agent.error || 'Failed';
      default:
        return 'Unknown';
    }
  };

  // Calculate elapsed time
  const getElapsedTime = () => {
    if (!agent.startedAt) return null;
    const start = new Date(agent.startedAt).getTime();
    const end = agent.completedAt ? new Date(agent.completedAt).getTime() : Date.now();
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const elapsedTime = getElapsedTime();

  return (
    <div className={`rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden transition-all ${config.bgColor}`}>
      {/* Main Content */}
      <div
        className="p-4 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          {/* Agent Icon */}
          <div className="text-2xl">{config.icon}</div>

          {/* Agent Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {agent.agentName}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">
                {agent.diagramType}
              </span>
            </div>
            <p className={`text-sm ${config.color} truncate`}>
              {getStatusText()}
            </p>
          </div>

          {/* Progress / Status */}
          <div className="flex items-center gap-3">
            {elapsedTime && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {elapsedTime}
              </span>
            )}
            {agent.status === 'analyzing' || agent.status === 'generating' ? (
              <div className="w-20">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300 ease-out"
                      style={{ width: `${agent.progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-8 text-right">
                    {agent.progress}%
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-xl">{config.icon}</span>
            )}

            {/* Expand Icon */}
            <svg
              className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700 pt-3">
          {/* Agent Insights */}
          {agent.insights && (
            <div className="mb-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInsights(!showInsights);
                }}
                className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Agent Insights
                <svg
                  className={`w-4 h-4 transition-transform ${showInsights ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showInsights && (
                <div className="mt-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {agent.insights}
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {agent.error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg text-sm text-red-700 dark:text-red-300">
              <div className="font-medium mb-1">Error:</div>
              {agent.error}
            </div>
          )}

          {/* Logs (if available) */}
          {agent.logs && agent.logs.length > 0 && (
            <div className="mt-3">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Activity Log
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {agent.logs.map((log, index) => (
                  <div
                    key={index}
                    className={`text-xs font-mono p-1 rounded ${
                      log.level === 'warn'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    <span className="text-slate-400 dark:text-slate-500">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>{' '}
                    {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentStatusCard;
